import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { TICK_FONT, drawRoundedRect } from './config';
import { formatAmount } from '/src/lib/helpers';
import { ChartTooltip, createExternalTooltip } from './ChartTooltip';

export default function HorizontalBarChart({ data }) {
    const [tooltipState, setTooltipState] = useState({ visible: false });

    const trackPlugin = useMemo(() => ({
        id: 'hbTrack',
        beforeDatasetsDraw(chart) {
            if (!data?.track_color) return;
            const { ctx } = chart;
            const meta   = chart.getDatasetMeta(0);
            const xStart = chart.scales.x.getPixelForValue(0);
            const xEnd   = chart.scales.x.getPixelForValue(chart.scales.x.max);
            const r      = data.cornered ? 4 : 0;
            ctx.save();
            ctx.fillStyle = data.track_color;
            meta.data.forEach(bar => {
                const { y, height } = bar.getProps(['y', 'height'], true);
                drawRoundedRect(ctx, xStart, y, xEnd - xStart, height, r);
                ctx.fill();
            });
            ctx.restore();
        },
    }), [data?.track_color, data?.cornered]);

    const labelPlugin = useMemo(() => ({
        id: 'hbLabels',
        afterDatasetsDraw(chart) {
            if (!data?.label_inside && !data?.pct_data?.length) return;
            const { ctx } = chart;
            const meta = chart.getDatasetMeta(0);
            const xMax = chart.scales.x.getPixelForValue(chart.scales.x.max);
            ctx.save();
            ctx.textAlign    = 'left';
            ctx.textBaseline = 'middle';
            meta.data.forEach((bar, i) => {
                const value = data.dataset[i]?.[1];
                if (value == null) return;
                const { x, y, base } = bar.getProps(['x', 'y', 'base'], true);
                const text     = (data.value_formatter || formatAmount)(value);
                const barWidth = x - base;
                if (data.label_inside) {
                    ctx.font      = 'bold 11px sans-serif';
                    const fits    = barWidth >= ctx.measureText(text).width + 16;
                    ctx.fillStyle = fits ? (data.label_colors?.[i] || '#fff') : '#5d656d';
                    ctx.fillText(text, fits ? base + 8 : x + 6, y);
                }
                if (data.pct_data?.[i]) {
                    ctx.font      = '11px sans-serif';
                    ctx.fillStyle = '#999';
                    ctx.fillText(data.pct_data[i], xMax + 8, y);
                }
            });
            ctx.restore();
        },
    }), [data?.label_inside, data?.label_colors, data?.pct_data, data?.dataset, data?.value_formatter]);

    const chartData = useMemo(() => {
        if (!data?.dataset) return null;
        return {
            labels: data.dataset.map(d => d[0]),
            datasets: [{
                data: data.dataset.map(d => d[1]),
                backgroundColor: data.color_table || [],
                borderRadius: data.cornered ? 4 : 0,
                borderSkipped: false,
                // caps how tall a bar gets when there are very few categories (e.g. a
                // single bar would otherwise stretch to fill the whole chart height)
                maxBarThickness: data.max_bar_thickness || 40,
            }],
        };
    }, [data]);

    const options = useMemo(() => {
        if (!data) return {};
        // optional popup-only override (e.g. extra precision the in-bar labels drop), falling back to value_formatter
        const tooltipFormatter = data.tooltip_value_formatter || data.value_formatter || formatAmount;
        return {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 1000 },
            layout: { padding: { right: data.margin_right || 60 } },
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: false,
                    external: createExternalTooltip(setTooltipState, (item) => ({
                        title: item.label,
                        lines: [`${data.tooltip_value_label ?? 'Capital'}: ${tooltipFormatter(item.raw)}`],
                    })),
                },
            },
            scales: {
                x: {
                    display: data.show_xAxis || false,
                    min: 0,
                    max: data.x_domain?.[1],
                    grid: { display: false },
                },
                y: {
                    display: true,
                    border: { display: !!data.show_left_line },
                    grid: { display: false },
                    ticks: {
                        color: '#888',
                        font: TICK_FONT,
                        crossAlign: data.left_align_y_labels ? 'far' : 'near',
                    },
                    // caps the label column to a share of the chart's current width so it doesn't crowd the bars on mobile
                    afterFit: scale => {
                        const desired = data.margin_left || 200;
                        scale.width = Math.min(desired, Math.max(90, scale.chart.width * 0.45));

                        // Chart.js doesn't ellipsize long labels on its own - truncate anything that no longer fits
                        const { ctx } = scale;
                        const maxLabelWidth = scale.width - 10;
                        ctx.font = `${TICK_FONT.size}px ${TICK_FONT.family}`;
                        scale.ticks.forEach(tick => {
                            const label = String(tick.label ?? '');
                            if (!label || ctx.measureText(label).width <= maxLabelWidth) return;

                            let truncated = label;
                            while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxLabelWidth) {
                                truncated = truncated.slice(0, -1);
                            }
                            tick.label = `${truncated}…`;
                        });
                    },
                },
            },
        };
    }, [data]);

    if (!chartData) return null;

    // dynamicHeight: `height` is a floor, chart flex-grows to fill its sibling's header height, like DonutChart
    const sizeStyle = data.dynamicHeight ? { minHeight: data.height || 0, flex: 1 }
        : data.height ? { height: data.height }
            : { flex: 1 };

    // react-chartjs-2 only registers `plugins` once, so keying on the dataset forces a remount to refresh them
    const chartKey = JSON.stringify(data.dataset);

    return (
        <div className={`chart-container chart-canvas${data.dynamicHeight ? ' chart-canvas--fill' : ''}`} style={sizeStyle}>
            <Bar key={chartKey} data={chartData} options={options} plugins={[trackPlugin, labelPlugin]} />
            <ChartTooltip state={tooltipState} />
        </div>
    );
}
